import {
  combineLatest,
  concatMap,
  Observable,
  share,
  shareReplay,
  ReplaySubject,
  timer,
  map,
  distinctUntilChanged,
  tap,
  switchMap,
  catchError,
  takeWhile,
  throwIfEmpty,
  firstValueFrom,
  filter,
} from "rxjs";

// This import has been flagged as unallowed for this class. It may be involved in a circular dependency loop.
// eslint-disable-next-line no-restricted-imports
import { KeyService, KdfConfigService } from "@bitwarden/key-management";
// eslint-disable-next-line no-restricted-imports
import { EncString } from "@bitwarden/legacy-crypto";
import { PerfTrackGroup } from "@bitwarden/logging";
import { ManagedSettingsService } from "@bitwarden/managed-settings";
import {
  PasswordManagerClient,
  ClientSettings,
  TokenProvider,
  UnsignedSharedKey,
  WrappedAccountCryptographicState,
  Kdf,
  V2UpgradeToken,
} from "@bitwarden/sdk-internal";

import { ApiService } from "../../../abstractions/api.service";
import { AccountInfo, AccountService } from "../../../auth/abstractions/account.service";
import { AccountCryptographicStateService } from "../../../key-management/account-cryptography/account-cryptographic-state.service";
import { JsWasmStateBridge } from "../../../key-management/state-bridge";
import { V2UpgradeTokenStateService } from "../../../key-management/upgrade-token/abstractions/v2-upgrade-token-state.service.abstraction";
import { OrganizationId, UserId } from "../../../types/guid";
import { UserKey } from "../../../types/key";
import { AppIdService } from "../../abstractions/app-id.service";
import { ConfigService } from "../../abstractions/config/config.service";
import { Environment, EnvironmentService } from "../../abstractions/environment.service";
import { LogService } from "../../abstractions/log.service";
import { PlatformUtilsService } from "../../abstractions/platform-utils.service";
import { SdkClientFactory } from "../../abstractions/sdk/sdk-client-factory";
import { SdkLoadService } from "../../abstractions/sdk/sdk-load.service";
import {
  asUuid,
  SdkService,
  SdkUnlockRequest,
  toSdkDevice,
  UserNotLoggedInError,
} from "../../abstractions/sdk/sdk.service";
import { compareValues } from "../../misc/compare-values";
import { Rc } from "../../misc/reference-counting/rc";
import { StateProvider } from "../../state";
import { toSdkFeatureFlags } from "../config/default-config.service";

import { initializeClientManagedState } from "./client-managed-state";

/**
 * A token provider that exposes the access token to the SDK.
 */
class JsTokenProvider implements TokenProvider {
  constructor(
    private apiServiceProvider: () => ApiService,
    private userId?: UserId,
  ) {}

  async get_access_token(): Promise<string | undefined> {
    if (this.userId == null) {
      return undefined;
    }

    return await this.apiServiceProvider().getActiveBearerToken(this.userId);
  }
}

const LIFETIME_TRACK_GROUP = PerfTrackGroup.Sdk;
const LIFETIME_TRACK = "Lifetime";
const CRYPTO_TRACK_GROUP = PerfTrackGroup.Unlock;
const CRYPTO_TRACK = "Crypto";

export class DefaultSdkService implements SdkService {
  private sdkClientCache = new Map<UserId, Observable<Rc<PasswordManagerClient>>>();

  client$ = this.environmentService.environment$.pipe(
    concatMap(async (env) => {
      await SdkLoadService.Ready;
      const settings = await this.toSettings(env);
      const managedSettings = await firstValueFrom(this.managedSettingsService.client$);
      const client = await this.sdkClientFactory.createSdkClient(
        new JsTokenProvider(this.apiServiceProvider),
        settings,
        managedSettings,
      );
      await this.loadFeatureFlags(client);
      return client;
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  version$ = this.client$.pipe(
    map((client) => client.version()),
    catchError(() => "Unsupported"),
  );

  constructor(
    private sdkClientFactory: SdkClientFactory,
    private environmentService: EnvironmentService,
    private platformUtilsService: PlatformUtilsService,
    private accountService: AccountService,
    private kdfConfigServiceProvider: () => KdfConfigService,
    private keyServiceProvider: () => KeyService,
    private accountCryptographyStateServiceProvider: () => AccountCryptographicStateService,
    private apiServiceProvider: () => ApiService,
    private stateProvider: StateProvider,
    private configServiceProvider: () => ConfigService,
    private v2UpgradeTokenStateService: V2UpgradeTokenStateService,
    private managedSettingsService: ManagedSettingsService,
    private appIdService: AppIdService,
    private logService: LogService,
    private userAgent: string | null = null,
  ) {}

  userClient$(userId: UserId): Observable<Rc<PasswordManagerClient>> {
    return this.internalClient$(userId).pipe(
      takeWhile((client) => client !== undefined, false),
      // Filter out clients that have been marked for disposal. This can happen in the
      // race window where `internalClient$`'s `combineLatest` re-emits (e.g. during
      // unlock when org keys / user key re-emit): the previous inner Observable's
      // cleanup marks the old Rc for disposal before the new client finishes its
      // async initialization, leaving the shared ReplaySubject holding a disposed
      // reference that would otherwise throw on `take()`.
      filter((client) => !client.isMarkedForDisposal),
      throwIfEmpty(() => new UserNotLoggedInError(userId)),
    );
  }

  async unlock(userId: UserId, request: SdkUnlockRequest): Promise<UserKey | null> {
    return null;
  }

  async lock(userId: UserId): Promise<void> {}

  logout(userId: UserId): void {}

  async setFlags(userId: UserId, flags: Map<string, boolean>): Promise<void> {}

  async setOrgKeys(
    userId: UserId,
    orgKeys: Record<OrganizationId, UnsignedSharedKey>,
  ): Promise<void> {}

  /**
   * Creates a client for a specific user by using the existing state of the application.
   * @param userId The user id for which to create the client
   * @returns An observable that emits the client for the user
   */
  private internalClient$(userId: UserId): Observable<Rc<PasswordManagerClient>> {
    const cached = this.sdkClientCache.get(userId);
    if (cached !== undefined) {
      return cached;
    }

    const account$ = this.accountService.accounts$.pipe(
      map((accounts) => accounts[userId]),
      distinctUntilChanged(),
    );
    const kdfParams$ = this.kdfConfigServiceProvider()
      .getKdfConfig$(userId)
      .pipe(distinctUntilChanged());
    const accountCryptographicState$ = this.accountCryptographyStateServiceProvider()
      .accountCryptographicState$(userId)
      .pipe(distinctUntilChanged());
    const keyService = this.keyServiceProvider();
    const userKey$ = keyService.userKey$(userId).pipe(distinctUntilChanged());
    const orgKeys$ = keyService.encryptedOrgKeys$(userId).pipe(
      distinctUntilChanged(compareValues), // The upstream observable emits different objects with the same values
    );
    const v2UpgradeToken$ = this.v2UpgradeTokenStateService
      .v2UpgradeToken$(userId)
      .pipe(distinctUntilChanged());

    const client$ = combineLatest([
      this.environmentService.getEnvironment$(userId),
      account$,
      kdfParams$,
      accountCryptographicState$,
      userKey$,
      orgKeys$,
      v2UpgradeToken$,
      SdkLoadService.Ready, // Makes sure we wait (once) for the SDK to be loaded
    ]).pipe(
      // switchMap is required to allow the clean-up logic to be executed when `combineLatest` emits a new value.
      switchMap(
        ([
          env,
          account,
          kdfParams,
          accountCryptographicState,
          userKey,
          orgKeys,
          v2UpgradeToken,
        ]) => {
          // Create our own observable to be able to implement clean-up logic
          return new Observable<Rc<PasswordManagerClient>>((subscriber) => {
            const createAndInitializeClient = async () => {
              if (env == null) {
                return undefined;
              }

              const settings = await this.toSettings(env);
              const managedSettings = await firstValueFrom(this.managedSettingsService.client$);

              const createMeasurement = this.logService.startMeasurement(
                LIFETIME_TRACK_GROUP,
                LIFETIME_TRACK,
                "create",
              );
              const client = await this.sdkClientFactory.createSdkClient(
                new JsTokenProvider(this.apiServiceProvider, userId),
                settings,
                managedSettings,
              );
              createMeasurement.finish();
              await this.initializeClient(userId, client);

              // Returns a locked SDK client, if any of these values are missing
              if (kdfParams == null || accountCryptographicState == null || userKey == null) {
                return client;
              }

              await this.initializeClientCrypto(
                userId,
                client,
                account,
                kdfParams.toSdkConfig(),
                accountCryptographicState,
                orgKeys,
                v2UpgradeToken,
              );

              return client;
            };

            let client: Rc<PasswordManagerClient> | undefined;
            createAndInitializeClient()
              .then((c) => {
                client = c === undefined ? undefined : new Rc(c);

                subscriber.next(client);
              })
              .catch((e) => {
                subscriber.error(e);
              });

            return () => {
              if (client == null) {
                return;
              }

              const disposeMeasurement = this.logService.startMeasurement(
                LIFETIME_TRACK_GROUP,
                LIFETIME_TRACK,
                "dispose",
              );
              client.markForDisposal();
              disposeMeasurement.finish();
            };
          });
        },
      ),
      tap({ finalize: () => this.sdkClientCache.delete(userId) }),
      share({
        connector: () => new ReplaySubject(1),
        resetOnRefCountZero: () => timer(1000),
      }),
    );

    this.sdkClientCache.set(userId, client$);
    return client$;
  }

  private async initializeClient(userId: UserId, client: PasswordManagerClient) {
    // Initialize the client managed repositories.
    await initializeClientManagedState(userId, client.platform().state(), this.stateProvider);
    client
      .km_state_bridge()
      .register_bridge_impl(new JsWasmStateBridge(this.stateProvider, userId));
    await this.loadFeatureFlags(client);
  }

  private async initializeClientCrypto(
    userId: UserId,
    client: PasswordManagerClient,
    account: AccountInfo,
    kdf: Kdf,
    accountCryptographicState: WrappedAccountCryptographicState,
    orgKeys: Record<OrganizationId, EncString>,
    v2UpgradeToken: V2UpgradeToken | null,
  ) {
    const userCryptoMeasurement = this.logService.startMeasurement(
      CRYPTO_TRACK_GROUP,
      CRYPTO_TRACK,
      "Decrypt User Keys",
    );
    await client.crypto().initialize_user_crypto({
      userId: asUuid(userId),
      email: account.email,
      method: { clientManagedState: {} },
      kdfParams: kdf,
      accountCryptographicState: accountCryptographicState,
      upgradeToken: v2UpgradeToken ?? undefined,
    });
    userCryptoMeasurement.finish();

    // We initialize the org crypto even if the org_keys are
    // null to make sure any existing org keys are cleared.
    const orgCryptoMeasurement = this.logService.startMeasurement(
      CRYPTO_TRACK_GROUP,
      CRYPTO_TRACK,
      "Decrypt Organization Keys",
    );
    await client.crypto().initialize_org_crypto({
      organizationKeys: new Map(
        Object.entries(orgKeys).map(([k, v]) => [asUuid(k), v.toJSON() as UnsignedSharedKey]),
      ),
    });
    orgCryptoMeasurement.finish([["Organizations", Object.keys(orgKeys).length]]);
  }

  private async loadFeatureFlags(client: PasswordManagerClient) {
    const serverConfig = await firstValueFrom(this.configServiceProvider().serverConfig$);
    await client.platform().load_flags(toSdkFeatureFlags(serverConfig));
  }

  private async toSettings(env: Environment): Promise<ClientSettings> {
    return {
      apiUrl: env.getApiUrl(),
      identityUrl: env.getIdentityUrl(),
      deviceType: toSdkDevice(this.platformUtilsService.getDevice()),
      deviceIdentifier: await this.appIdService.getAppId(),
      bitwardenClientVersion: await this.platformUtilsService.getApplicationVersionNumber(),
      userAgent: this.userAgent ?? navigator.userAgent,
    };
  }
}
